<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
        
        <!-- Mathematical Equations Background -->
        <div class="math-equations">
          <div class="equation equation-1">∫ f(x)dx = F(x) + C</div>
          <div class="equation equation-2">e^(iπ) + 1 = 0</div>
          <div class="equation equation-3">∇ × E = -∂B/∂t</div>
          <div class="equation equation-4">∑(n=1→∞) 1/n² = π²/6</div>
          <div class="equation equation-5">φ = (1 + √5)/2</div>
          <div class="equation equation-6">∂²u/∂t² = c²∇²u</div>
          <div class="equation equation-7">∫₀^∞ e^(-x²)dx = √π/2</div>
          <div class="equation equation-8">ζ(s) = ∑(n=1→∞) 1/n^s</div>
          <div class="equation equation-9">∇ · E = ρ/ε₀</div>
          <div class="equation equation-10">Γ(n) = (n-1)!</div>
        </div>
      </div>
      
      <div class="app-title-container">
        <h1 class="app-title">Budgeteer</h1>
        <div class="title-subtitle">Smart Budget Management</div>
        <div class="beta-badge">
          <span class="beta-text">BETA</span>
        </div>
      </div>

      <div class="login-container">
        <div class="login-header">
          <h2 class="login-title">Login</h2>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-fields-stack">
            <div class="login-input-group">
              <ion-label>Username</ion-label>
              <input 
                v-model="username" 
                type="text" 
                placeholder="Enter username"
                class="login-custom-input"
                :class="{ 'input-error': usernameError }"
                required
                aria-label="Username"
                @input="clearUsernameError"
              />
              <div v-if="usernameError" class="input-error-message">
                <ion-icon :icon="alertCircle" size="small"></ion-icon>
                <span>{{ usernameError }}</span>
              </div>
            </div>

            <div class="login-input-group">
              <ion-label>Password</ion-label>
              <input 
                v-model="password" 
                type="password" 
                placeholder="Enter password"
                class="login-custom-input"
                :class="{ 'input-error': passwordError }"
                required
                aria-label="Password"
                @input="clearPasswordError"
              />
              <div v-if="passwordError" class="input-error-message">
                <ion-icon :icon="alertCircle" size="small"></ion-icon>
                <span>{{ passwordError }}</span>
              </div>
            </div>

            <div class="login-input-group options-group">
              <div class="options-row">
                <label class="remember-me">
                  <input type="checkbox" aria-label="Remember me">
                  <span>Remember me</span>
                </label>
                <a href="#" class="forgot-link" aria-label="Forgot password">Forgot Password?</a>
              </div>
            </div>
          </div>

          <div class="login-form-actions">
            <ion-button 
              type="submit" 
              expand="block" 
              class="login-button"
              :class="{ 'loading': isLoading }"
              :disabled="isLoading"
              aria-label="Login"
            >
              <div v-if="isLoading" class="loading-spinner">
                <ion-spinner name="crescent"></ion-spinner>
              </div>
              <span v-else>Login</span>
            </ion-button>

            <div class="register-link">
              Don't have an account? 
              <router-link to="/register" aria-label="Register">Register</router-link>
            </div>
          </div>
        </form>
      </div>

      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, IonLabel, IonButton, IonIcon, IonSpinner } from '@ionic/vue';
import { alertCircle, close, checkmark } from 'ionicons/icons';
import axios from 'axios';
import { useProfileStore } from '@/store/profile';
import { mapActions } from 'pinia';
import {getAPIURL} from '../logic/api';

export default {
  name: 'LoginPage',
  components: {
    IonPage,
    IonContent,
    IonLabel,
    IonButton,
    IonIcon,
    IonSpinner
  },
  setup() {
    const profileStore = useProfileStore();
    return { profileStore };
  },
  data() {
    return {
      username: '',
      password: '',
      isLoading: false,
      usernameError: '',
      passwordError: '',
      alertCircle,
      close,
      checkmark,
      showToast: false,
      toastMessage: '',
      toastType: 'error',
      toastIcon: alertCircle
    }
  },
  methods: {
    ...mapActions(useProfileStore, ['fetchProfiles', 'setCurrentProfile']),
    
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    
    validateForm() {
      let isValid = true;
      
      // Clear previous field errors only
      this.clearUsernameError();
      this.clearPasswordError();
      
      // Validate username
      if (!this.username.trim()) {
        this.usernameError = 'Username is required';
        isValid = false;
      } else if (this.username.trim().length < 3) {
        this.usernameError = 'Username must be at least 3 characters';
        isValid = false;
      }
      
      // Validate password
      if (!this.password) {
        this.passwordError = 'Password is required';
        isValid = false;
      } else if (this.password.length < 5) {
        this.passwordError = 'Password must be at least 5 characters';
        isValid = false;
      }
      
      return isValid;
    },
    
    clearUsernameError() {
      this.usernameError = '';
    },
    
    clearPasswordError() {
      this.passwordError = '';
    },
    
    async handleLogin() {
      // Validate form first
      if (!this.validateForm()) {
        return;
      }
      
      this.isLoading = true;
      this.clearUsernameError();
      this.clearPasswordError();
      
      try {
        const response = await axios.post(getAPIURL()+'/api/user/login', {
          username: this.username.trim(),
          password: this.password
        });
        
        // Store login data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userID', response.data.userID);
        
        // Immediately fetch profiles and set the full current profile for sidebar/dashboard
        try {
          await this.fetchProfiles();
          const userID = response.data.userID;
          const userProfile = this.profileStore?.profiles?.find(p => p.id == userID || p._id == userID);
          if (userProfile) {
            this.setCurrentProfile(userProfile);
          } else {
            this.setCurrentProfile({ id: userID });
          }
        } catch (e) {
          // Fallback to minimal profile if fetching fails
          this.setCurrentProfile({ id: response.data.userID });
        }
        
        this.showSuccessToast('Login successful!');
        this.$router.push('/dashboard');
        
      } catch (error) {
        console.error('Login failed:', error);
        
        // Handle different types of errors
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const message = error.response.data?.message || 'Login failed';
          
          switch (status) {
            case 401:
              this.showErrorToast('Invalid username or password. Please try again.');
              break;
            case 404:
              this.showErrorToast('User not found. Please check your username.');
              break;
            case 500:
              this.showErrorToast('Server error. Please try again later.');
              break;
            default:
              this.showErrorToast(message);
          }
        } else if (error.request) {
          // Network error
          this.showErrorToast('Network error. Please check your connection and try again.');
        } else {
          // Other error
          this.showErrorToast('An unexpected error occurred. Please try again.');
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script> 